import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { CloseOutlined, EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import type { Theme } from '../../contexts/themeTypes';
import type { Map as MapboxMap } from 'mapbox-gl';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';

interface PiPCircuitViewProps {
  isVisible: boolean;
  mapTileProvider: MapTileProvider;
  tilesLength: number;
  tioTimestampsLength: number;
  pipCardRef: React.RefObject<HTMLDivElement | null>;
  pipMapContainerRef: React.RefObject<HTMLDivElement | null>;
  cardPosition: { x: number; y: number } | null;
  draggingCard: string | null;
  onCardMouseDown: (cardId: string, e: React.MouseEvent, currentPosition: { x: number; y: number } | null, elementRef?: HTMLElement | null) => void;
  onClose: () => void;
  size: { width: number; height: number };
  onSizeChange: (size: { width: number; height: number }) => void;
  pipMapRef: React.RefObject<MapboxMap | null>;
  circuitCenter: { lat: number; lon: number } | null;
  /** Mapbox control styling on the PiP map (matches main map basemap light/dark) */
  mapBasemapTheme: Theme;
}

export const PiPCircuitView = ({
  isVisible,
  mapTileProvider,
  tilesLength,
  tioTimestampsLength,
  pipCardRef,
  pipMapContainerRef,
  cardPosition,
  draggingCard,
  onCardMouseDown,
  onClose,
  size,
  onSizeChange,
  pipMapRef,
  circuitCenter,
  mapBasemapTheme,
}: PiPCircuitViewProps) => {
  const { theme } = useTheme();
  const mapChrome = mapBasemapTheme;
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Handler for centering on circuit
  const handleCenterOnCircuit = useCallback(() => {
    if (pipMapRef.current && circuitCenter) {
      pipMapRef.current.flyTo({
        center: [circuitCenter.lon, circuitCenter.lat],
      });
    }
  }, [pipMapRef, circuitCenter]);

  // Handler for resetting map view
  const handleResetMapView = useCallback(() => {
    if (pipMapRef.current && circuitCenter) {
      pipMapRef.current.flyTo({
        center: [circuitCenter.lon, circuitCenter.lat],
        zoom: 13,
        bearing: 0,
        pitch: 0,
      });
    }
  }, [pipMapRef, circuitCenter]);

  // Resize map when card size changes
  useEffect(() => {
    if (pipMapRef.current && pipMapRef.current.resize) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        pipMapRef.current?.resize();
      }, 0);
    }
  }, [size.width, size.height, pipMapRef]);

  const handleResizeStart = (handle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeHandle(handle);
    if (pipCardRef.current) {
      const rect = pipCardRef.current.getBoundingClientRect();
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
      };
    }
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current || !pipCardRef.current) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;

      const minWidth = 300;
      const minHeight = 200;
      const maxWidth = 800;
      const maxHeight = 600;

      switch (resizeHandle) {
        case 'se': // Southeast (bottom-right)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width + deltaX));
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height + deltaY));
          break;
        case 'sw': // Southwest (bottom-left)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width - deltaX));
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height + deltaY));
          break;
        case 'ne': // Northeast (top-right)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width + deltaX));
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height - deltaY));
          break;
        case 'nw': // Northwest (top-left)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width - deltaX));
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height - deltaY));
          break;
        case 'e': // East (right)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width + deltaX));
          break;
        case 'w': // West (left)
          newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStartRef.current.width - deltaX));
          break;
        case 's': // South (bottom)
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height + deltaY));
          break;
        case 'n': // North (top)
          newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartRef.current.height - deltaY));
          break;
      }

      onSizeChange({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeHandle(null);
      resizeStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeHandle, onSizeChange, pipCardRef]);

  if (!isVisible || (((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && tilesLength === 0) || (isTioProvider(mapTileProvider) && tioTimestampsLength === 0))) {
    return null;
  }

  const resizeHandleStyle: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
    zIndex: 1003,
    transition: 'background-color 0.2s',
  };

  return (
    <div
      ref={pipCardRef}
      onMouseDown={(e) => {
        // Don't start dragging if clicking on resize handle
        if ((e.target as HTMLElement).classList.contains('resize-handle')) {
          return;
        }
        onCardMouseDown('pip', e, cardPosition, pipCardRef.current);
      }}
      style={{
        position: 'absolute',
        left: cardPosition?.x ?? 0,
        top: cardPosition?.y ?? 100,
        zIndex: draggingCard === 'pip' ? 2000 : 1002,
        width: size.width,
        height: size.height,
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        border: `2px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        cursor: draggingCard === 'pip' ? 'grabbing' : 'default',
      }}
    >
      {/* Header with drag handle */}
      <div
        className="drag-handle"
        style={{
          padding: '8px 12px',
          backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Circuit View
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
            minWidth: 24,
            padding: '0 4px',
          }}
        />
      </div>

      {/* Map container */}
      <div
        ref={pipMapContainerRef}
        style={{
          width: '100%',
          height: 'calc(100% - 37px)', // Subtract header height
          cursor: 'default',
        }}
      />

      {/* Map Controls Toolbar - Center on Circuit and Reset View (below Mapbox NavigationControl) */}
      <div style={{
        position: 'absolute',
        top: 150, // Below header (~37px) + Mapbox nav control (~90px) + gap
        right: 10,
        zIndex: 1003,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}>
        <div className="mapboxgl-ctrl mapboxgl-ctrl-group" style={{
          backgroundColor: mapChrome === 'dark' ? '#1f1f1f' : '#fff',
          boxShadow: mapChrome === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
          borderRadius: 4,
        }}>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleCenterOnCircuit}
            title="Center on Circuit"
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              borderBottom: mapChrome === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
            }}
          >
            <EnvironmentOutlined style={{ fontSize: '15px', color: mapChrome === 'dark' ? '#fff' : '#333' }} />
          </button>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleResetMapView}
            title="Reset Map View"
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <AimOutlined style={{ fontSize: '15px', color: mapChrome === 'dark' ? '#fff' : '#333' }} />
          </button>
        </div>
      </div>

      {/* Resize handles */}
      {/* Corner handles */}
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('nw', e)}
        style={{
          ...resizeHandleStyle,
          top: 0,
          left: 0,
          width: '12px',
          height: '12px',
          cursor: 'nwse-resize',
          borderTopLeftRadius: '12px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('ne', e)}
        style={{
          ...resizeHandleStyle,
          top: 0,
          right: 0,
          width: '12px',
          height: '12px',
          cursor: 'nesw-resize',
          borderTopRightRadius: '12px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('sw', e)}
        style={{
          ...resizeHandleStyle,
          bottom: 0,
          left: 0,
          width: '12px',
          height: '12px',
          cursor: 'nesw-resize',
          borderBottomLeftRadius: '12px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('se', e)}
        style={{
          ...resizeHandleStyle,
          bottom: 0,
          right: 0,
          width: '12px',
          height: '12px',
          cursor: 'nwse-resize',
          borderBottomRightRadius: '12px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />

      {/* Edge handles */}
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('n', e)}
        style={{
          ...resizeHandleStyle,
          top: 0,
          left: '12px',
          right: '12px',
          height: '8px',
          cursor: 'ns-resize',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('s', e)}
        style={{
          ...resizeHandleStyle,
          bottom: 0,
          left: '12px',
          right: '12px',
          height: '8px',
          cursor: 'ns-resize',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('e', e)}
        style={{
          ...resizeHandleStyle,
          right: 0,
          top: '12px',
          bottom: '12px',
          width: '8px',
          cursor: 'ew-resize',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('w', e)}
        style={{
          ...resizeHandleStyle,
          left: 0,
          top: '12px',
          bottom: '12px',
          width: '8px',
          cursor: 'ew-resize',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }}
      />
    </div>
  );
};
